import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Shield, CheckCircle2, FileText, Languages, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import BottomNav from "@/components/BottomNav";
import Footer from "@/components/Footer";
import { OnboardingOverlay } from "@/components/OnboardingOverlay";
import ConversionBlock from "@/components/ConversionBlock";
import customIcon from "@/assets/icon.jpg";
import featureOcr from "@/assets/feature-ocr.jpg";
import featureAi from "@/assets/feature-ai.jpg";
import featurePrivacy from "@/assets/feature-privacy.jpg";
import showcaseMobile from "@/assets/showcase-mobile.jpg";

const Home = () => {
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 400], [0, 8]);
  // Onboarding intro disabled by default to reduce first-use friction.
  // Flip to true to re-enable the OnboardingOverlay carousel.
  const showOnboarding = false;
  return <div className="min-h-screen bg-background pb-20">
      <Helmet>
        <title>Free OCR Online – Image & PDF to Text | FactScan AI</title>
        <meta 
          name="description" 
          content="FactScan AI is a free OCR online tool that extracts text from images, PDFs, and scanned documents instantly. Convert image to text with AI-powered OCR." 
        />
        <link rel="canonical" href="https://factscanai.app/" />
        <meta property="og:title" content="Free OCR Online – Image & PDF to Text | FactScan AI" />
        <meta property="og:description" content="Free AI OCR online. Extract text from images and PDFs, convert to Word or Excel, and summarize with AI in seconds. No signup required." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://factscanai.app/" />
        <meta property="og:image" content="https://factscanai.app/hero-premium-scan.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free OCR Online – Image & PDF to Text | FactScan AI" />
        <meta name="twitter:description" content="Free AI OCR online. Extract text from images and PDFs, convert to Word or Excel, and summarize with AI in seconds." />
        <meta name="twitter:image" content="https://factscanai.app/hero-premium-scan.jpg" />
      </Helmet>
      {showOnboarding && <OnboardingOverlay />}
      {/* Hero Section - Premium SaaS */}
      <section className="relative overflow-hidden">
        {/* Radial Spotlight - Soft ambient glow */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_-5%,_hsl(var(--primary)/0.10),_transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_15%,_hsl(var(--primary)/0.05),_transparent_40%)]" />
        </div>
        
        <div className="container relative mx-auto px-4 pt-14 pb-10 md:pt-24 md:pb-20">
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
            {/* Logo Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/8 rounded-full border border-primary/15 backdrop-blur-sm mb-5"
            >
              <img src={customIcon} alt="FactScan AI" className="w-4 h-4" />
              <span className="text-[10px] font-semibold text-primary tracking-widest uppercase">FactScan AI</span>
            </motion.div>

            {/* Headline - Confident, premium */}
            <h1
              className="text-[26px] sm:text-[32px] md:text-[42px] lg:text-[50px] font-bold tracking-[-0.025em] leading-[1.12] mb-4 md:mb-5"
            >
              <span className="text-foreground">Scan anything.</span>
              <br className="hidden sm:block" />
              <span className="text-foreground"> Convert it to editable Word or Excel instantly.</span>
            </h1>

            {/* Subheadline - Clear value prop */}
            <motion.p 
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.12, ease: "easeOut" }}
              className="text-[14px] sm:text-[15px] md:text-[17px] text-muted-foreground max-w-md md:max-w-xl leading-[1.7] font-normal mb-7 md:mb-9"
            >
              Fast, browser-based scanning. Download as editable Word (DOCX) or Excel in seconds.
            </motion.p>

            {/* Core Value Statement */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
              className="text-[13px] sm:text-sm md:text-[15px] text-muted-foreground max-w-md md:max-w-xl leading-relaxed font-normal -mt-4 md:-mt-6 mb-7 md:mb-9"
            >
              Convert PDFs, images, contracts, and receipts into editable Word or Excel in seconds.
            </motion.p>

            {/* CRO: Export value line */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.17, ease: "easeOut" }}
              className="text-[13px] sm:text-sm md:text-[15px] text-muted-foreground max-w-md md:max-w-xl leading-relaxed font-medium -mt-4 md:-mt-6 mb-6 md:mb-8"
            >
              Extract text instantly and download it as an editable Word or Excel file.
            </motion.p>

            {/* CTA Block */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.18, ease: "easeOut" }}
              className="flex flex-col items-center gap-2"
            >
              {/* Workflow - moved above CTA for above-the-fold clarity */}
              <p className="text-[11px] md:text-xs text-muted-foreground font-medium tracking-wide mb-1">
                Upload <span className="text-primary">→</span> extract <span className="text-primary">→</span> edit in Word <span className="text-primary">→</span> download
              </p>
              <p className="text-[12px] md:text-sm text-muted-foreground mb-1">
                Upload your PDF, image, or document to start
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Link to="/scan">
                  <Button size="lg" className="text-[15px] md:text-base px-10 md:px-14 py-5 md:py-6 bg-gradient-to-r from-primary via-primary to-accent shadow-[0_6px_28px_hsl(var(--primary)/0.30)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_12px_40px_hsl(var(--primary)/0.45)] font-semibold tracking-wide rounded-xl">
                    Upload file
                    <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                </Link>
                <Link to="/use-cases">
                  <Button variant="outline" size="lg" className="text-[15px] md:text-base px-6 md:px-8 py-5 md:py-6 border-border/60 hover:border-primary/40 hover:bg-primary/5 font-medium rounded-xl transition-all duration-200 opacity-90">
                    See Real Output
                  </Button>
                </Link>
              </div>
              <p className="text-[11px] md:text-xs text-muted-foreground mt-1">
                Drag & drop your file to start
              </p>
              <p className="text-[11px] md:text-xs text-muted-foreground mt-1">
                Edit your document in Word after scanning — download as DOCX or Excel
              </p>
              <div className="flex flex-col items-center" style={{ marginTop: '4px', marginBottom: '6px' }}>
                <Link to="/tools">
                  <Button
                    variant="ghost"
                    className="text-[12px] md:text-[13px] px-3.5 md:px-4 py-1.5 md:py-2 border border-primary/25 text-primary/75 hover:bg-primary/5 hover:border-primary/45 font-medium rounded-xl transition-all duration-200 opacity-80"
                  >
                    Explore 100+ Free Tools →
                  </Button>
                </Link>
                <p className="text-[10px] md:text-[11px] text-muted-foreground text-center" style={{ marginTop: '4px' }}>
                  OCR, PDFs, handwriting, receipts & more
                </p>
              </div>
              <div className="flex flex-col items-center gap-0.5 text-center -mt-1">
                <p className="text-[11px] md:text-xs text-muted-foreground font-semibold tracking-wide">
                  Free · No signup for your first extraction · Browser-based · Works on any device
                </p>
                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-2 max-w-md">
                  <span className="text-[10px] md:text-[11px] text-foreground/75">Built for finance & accounting workflows:</span>
                  <Link to="/bank-statement-to-excel" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">Bank Statement to Excel</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/invoice-data-extraction-software" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">Invoice Data Extraction</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/receipt-data-extraction-ai" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">Receipt Data Extraction</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/ai-pdf-data-extraction" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">AI PDF Data Extraction</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/receipt-to-excel" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">Receipt to Excel</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/document-data-extraction" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">Document Data Extraction</Link>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-2 max-w-md">
                  <span className="text-[10px] md:text-[11px] text-foreground/75">Popular OCR tools:</span>
                  <Link to="/document-scanner" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">Document Scanner</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/ocr-online" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">OCR Online</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/no-signup-ocr" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">OCR Without Signup</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/handwriting-to-text" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">Handwriting to Text</Link>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <Link to="/image-translator-online" className="text-[10px] md:text-[11px] text-primary/85 hover:text-primary hover:underline font-medium">Image Translator</Link>
                </div>
                <p className="text-[10px] md:text-[11px] text-muted-foreground">
                  Free preview available — download requires upgrade
                </p>
                <p className="inline-flex items-center gap-1.5 text-[10px] md:text-[11px] text-muted-foreground">
                  <Shield className="w-2.5 h-2.5 text-primary" strokeWidth={2} />
                  Files are sent to FactScan's OCR service to extract the text.
                </p>
              </div>
            </motion.div>

            {/* Trust Line */}
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.26 }}
              className="text-[10px] md:text-[11px] text-muted-foreground font-medium tracking-widest uppercase mt-7 md:mt-9"
            >
              Trusted by students, professionals, and teams worldwide
            </motion.p>

            {/* Hero Image */}
            <motion.div 
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.30, ease: "easeOut" }}
              style={{ y: heroImageY }}
              className="relative mt-10 md:mt-14 w-full max-w-2xl"
            >
              {/* Ambient Glow */}
              <div className="absolute -inset-8 bg-gradient-to-t from-primary/8 via-primary/4 to-transparent rounded-3xl blur-3xl" />
              
              {/* Image Container */}
              <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.35),_0_0_0_1px_hsl(var(--primary)/0.06)]">
                <img
                  src="/hero-premium-scan.jpg"
                  alt="AI Document Scanning"
                  width={1330}
                  height={749}
                  fetchPriority="high"
                  className="relative w-full max-h-[260px] md:max-h-[360px] object-cover object-top"
                />
              </div>
            </motion.div>

            {/* Micro-Benefits Row */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.38 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mt-8 md:mt-10 w-full max-w-2xl"
            >
              {[{
              icon: CheckCircle2,
              label: "99% OCR accuracy"
            }, {
              icon: Layers,
              label: "Multi-page scanning"
            }, {
              icon: FileText,
              label: "Instant summaries"
            }, {
              icon: Languages,
              label: "Auto translation"
            }].map(({
              icon: Icon,
              label
            }, idx) => <div key={idx} className="flex flex-col items-center gap-1.5 py-2.5 px-2 rounded-lg bg-card/30 border border-border/30 hover:border-primary/20 transition-colors">
                  <Icon className="w-4 h-4 text-primary/80" strokeWidth={1.5} />
                  <span className="text-[10px] md:text-[11px] font-medium text-muted-foreground">{label}</span>
                </div>)}
            </motion.div>
          </div>
        </div>
      </section>

      <ConversionBlock />

      {/* How it works */}
      <section className="container mx-auto px-4 py-14 md:py-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">How it works</h2>
          <p className="text-muted-foreground text-center mb-10 text-sm md:text-base">Three simple steps from document to editable output.</p>
          <div className="grid sm:grid-cols-3 gap-4 md:gap-5">
            {[
              { n: "1", title: "Upload your document", desc: "PDF, image, screenshot, or scanned file." },
              { n: "2", title: "AI extracts text and data", desc: "Accurate OCR with smart structure detection." },
              { n: "3", title: "Download or export instantly", desc: "Word, Excel, PDF, or plain text in one click." },
            ].map((s) => (
              <div key={s.n} className="bg-card/50 border border-border/50 rounded-xl p-5 hover:border-primary/30 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/15 flex items-center justify-center mb-3 text-primary font-semibold text-sm">{s.n}</div>
                <h3 className="text-base font-semibold mb-1.5 text-foreground">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you can do */}
      <section className="container mx-auto px-4 pb-14 md:pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">What you can do</h2>
          <p className="text-muted-foreground text-center mb-10 text-sm md:text-base">A complete toolkit for documents, images, and data.</p>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {[
              "Convert PDFs to Word or Excel",
              "Extract text from images and screenshots",
              "Scan receipts, invoices, and contracts",
              "Work with handwritten notes",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 bg-card/40 border border-border/40 rounded-xl p-4 hover:border-primary/30 transition-colors">
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                </div>
                <span className="text-sm md:text-[15px] text-foreground/85">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular tools */}
      <section className="container mx-auto px-4 pb-4 md:pb-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Popular tools</h2>
          <p className="text-muted-foreground text-center mb-10 text-sm md:text-base">Jump straight to the tool you need.</p>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {[
              { label: "PDF to Word OCR", href: "/pdf-to-word-ocr" },
              { label: "PDF to DOCX Converter", href: "/pdf-to-docx-converter" },
              { label: "Extract Contract Text", href: "/extract-contract-text-from-pdf" },
              { label: "Invoice to Excel", href: "/invoice-to-excel" },
            ].map((t) => (
              <Link
                key={t.href}
                to={t.href}
                className="group flex items-center justify-between bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:-translate-y-0.5 transition-all"
              >
                <span className="text-sm md:text-[15px] font-medium text-foreground">{t.label}</span>
                <ArrowRight className="w-4 h-4 text-primary/70 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular OCR Tools */}
      <section className="container mx-auto px-4 pb-14 md:pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Popular OCR Tools</h2>
          <p className="text-muted-foreground text-center mb-10 text-sm md:text-base">
            Free, browser-based OCR for every format.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {[
              { label: "Handwriting to Text", href: "/handwriting-to-text" },
              { label: "OCR Online", href: "/ocr-online" },
              { label: "No Signup OCR", href: "/no-signup-ocr" },
              { label: "Extract Data from Invoices", href: "/extract-data-from-invoices" },
              { label: "PDF to Text OCR", href: "/pdf-to-text-ocr" },
              { label: "Free JPG to Text Online", href: "/free-jpg-to-text-online" },
            ].map((t) => (
              <Link
                key={t.href}
                to={t.href}
                className="group flex items-center justify-between bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:-translate-y-0.5 transition-all"
              >
                <span className="text-sm md:text-[15px] font-medium text-foreground">{t.label}</span>
                <ArrowRight className="w-4 h-4 text-primary/70 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Polished */}
      <section className="container mx-auto px-4 py-24 md:py-32">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">Powerful Features</h2>
        <p className="text-muted-foreground text-center mb-12 max-w-lg mx-auto text-sm md:text-base">
          Everything you need to scan, understand, and work with documents efficiently.
        </p>
        
        {/* 4-Block Feature Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-5xl mx-auto mb-20">
          <div className="group relative bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(30,144,255,0.12)]">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/15 transition-colors">
              <Zap className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-base font-semibold mb-1.5 text-foreground">OCR Accuracy</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">99% accurate text extraction from any document or image</p>
          </div>
          
          <div className="group relative bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(30,144,255,0.12)]">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/15 transition-colors">
              <img src={customIcon} alt="FactScan AI" className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold mb-1.5 text-foreground">AI Summary</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">Instant intelligent summaries and key action points</p>
          </div>
          
          <div className="group relative bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(30,144,255,0.12)]">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/15 transition-colors">
              <Languages className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-base font-semibold mb-1.5 text-foreground">Translate & Rewrite</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">Auto-translate to 50+ languages and rewrite content</p>
          </div>
          
          <div className="group relative bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(30,144,255,0.12)]">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/15 transition-colors">
              <FileText className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-base font-semibold mb-1.5 text-foreground">Export</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">Save as PDF, DOCX, or TXT with one tap</p>
          </div>
        </div>

        {/* Credibility Section */}
        <div className="max-w-4xl mx-auto mb-20">
          <h3 className="text-xl md:text-2xl font-bold text-center mb-10">Why users choose FactScan AI</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[{
            icon: CheckCircle2,
            title: "Trusted OCR quality",
            desc: "Industry-leading accuracy you can rely on"
          }, {
            icon: "custom",
            title: "AI that explains documents",
            desc: "Not just scans—real understanding"
          }, {
            icon: Shield,
            title: "Built for speed + privacy",
            desc: "Fast results, local processing first"
          }].map(({
            icon,
            title,
            desc
          }, idx) => <div key={idx} className="flex flex-col items-center text-center py-6 px-4 rounded-xl bg-card/50 border border-border/50">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3 border border-primary/15">
                  {icon === "custom" ? <img src={customIcon} alt="FactScan AI" className="w-6 h-6" /> : icon === CheckCircle2 ? <CheckCircle2 className="w-6 h-6 text-primary" strokeWidth={1.5} /> : <Shield className="w-6 h-6 text-primary" strokeWidth={1.5} />}
                </div>
                <h4 className="text-base font-semibold mb-1 text-foreground">{title}</h4>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </div>)}
          </div>
        </div>
        
        {/* Feature 1: OCR */}
        <div className="grid md:grid-cols-2 gap-10 items-center mb-20 max-w-5xl mx-auto">
          <div className="order-2 md:order-1">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5 border border-primary/15">
              <Zap className="w-6 h-6 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold mb-3">Advanced OCR Technology</h3>
            <p className="text-muted-foreground text-base leading-relaxed mb-5">
              Extract text from images, receipts, business cards, and documents instantly. Our advanced OCR engine supports multiple languages with 99% accuracy.
            </p>
            <ul className="space-y-2.5">
              {["Multi-language support", "99% accuracy rate", "Offline processing", "Instant results"].map((item, idx) => <li key={idx} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="w-2.5 h-2.5 text-primary" />
                  </div>
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="order-1 md:order-2">
            <img src={featureOcr} alt="OCR Text Extraction" width={1000} height={680} loading="lazy" decoding="async" className="rounded-xl shadow-lg border border-border" />
          </div>
        </div>

        {/* Feature 2: AI Analysis */}
        <div className="grid md:grid-cols-2 gap-10 items-center mb-20 max-w-5xl mx-auto">
          <div>
            <img src={featureAi} alt="AI Analysis" width={1000} height={680} loading="lazy" decoding="async" className="rounded-xl shadow-lg border border-border" />
          </div>
          <div>
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5 border border-primary/15">
              <img src={customIcon} alt="FactScan AI" className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold mb-3">AI-Powered Intelligence</h3>
            <p className="text-muted-foreground text-base leading-relaxed mb-5">
              Get intelligent summaries, key action points, and automatic translations. Our AI understands context and extracts the most important information.
            </p>
            <ul className="space-y-2.5">
              {["Smart summarization", "Key point extraction", "Auto translation", "Context awareness"].map((item, idx) => <li key={idx} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="w-2.5 h-2.5 text-primary" />
                  </div>
                  {item}
                </li>)}
            </ul>
          </div>
        </div>

        {/* Feature 3: Privacy */}
        <div className="grid md:grid-cols-2 gap-10 items-center mb-16 max-w-5xl mx-auto">
          <div className="order-2 md:order-1">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5 border border-primary/15">
              <Shield className="w-6 h-6 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold mb-3">Privacy-First Design</h3>
            <p className="text-muted-foreground text-base leading-relaxed mb-5">
              Files are sent to FactScan's OCR service to extract text, and AI analysis only runs when you explicitly request it.
            </p>
            <ul className="space-y-2.5">
              {["Encrypted in transit", "AI analysis only on request", "Full user control"].map((item, idx) => <li key={idx} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ArrowRight className="w-2.5 h-2.5 text-primary" />
                  </div>
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="order-1 md:order-2">
            <img src={featurePrivacy} alt="Privacy & Security" width={1000} height={680} loading="lazy" decoding="async" className="rounded-xl shadow-lg border border-border" />
          </div>
        </div>
        
        {/* Mobile Showcase */}
        <div className="max-w-3xl mx-auto mt-24">
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">Designed for Mobile</h3>
            <p className="text-muted-foreground text-base">
              Beautiful, intuitive interface optimized for your smartphone
            </p>
          </div>
          <div className="flex justify-center">
            <img src={showcaseMobile} alt="Mobile App Interface" width={640} height={941} loading="lazy" decoding="async" className="rounded-2xl shadow-lg border border-border max-w-xs" />
          </div>
        </div>
        
        {/* Use Cases Link */}
        <div className="text-center mt-12">
          <Link to="/use-cases">
            <Button size="lg" className="gap-2 bg-gradient-to-r from-primary to-accent hover:opacity-90 rounded-xl">
              Explore All Use Cases
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section - Premium */}
      <section className="container mx-auto px-4 py-20 md:py-28">
        <div className="bg-gradient-to-br from-primary via-primary to-accent rounded-2xl p-10 md:p-14 text-center max-w-3xl mx-auto shadow-[0_20px_60px_rgba(30,144,255,0.25)]">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-primary-foreground">
            Ready to scan smarter?
          </h2>
          <p className="text-base md:text-lg text-primary-foreground/85 mb-8 max-w-xl mx-auto leading-relaxed">
            Join thousands of users who scan and understand documents instantly with AI.
          </p>
          <Link to="/scan">
            <Button size="lg" className="text-base px-8 py-5 bg-background text-foreground hover:bg-background/90 font-semibold rounded-xl shadow-lg">
              Get Started Free
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <BottomNav />
    </div>;
};
export default Home;